import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { push } from 'react-router-redux'
import moment from 'moment'
import classNames from 'classnames'

import { sendButtonClickSoundCommand, sendClickSoundCommand, sendHoverSoundCommand } from 'react-wot-bootstrap'

import { toRoman } from 'helpers'
import { getEndDateMessage } from 'helpers/datetime'
import { INDUSTRIAL_RESOURCE_MULTIPLIER, STRONGHOLD } from 'helpers/i18n'

import { requestNextChallengeInfo } from 'state/actions/challenges'
import { playEnterStrongholdSound, sendEnterStrongholdSoundState } from 'state/actions/sounds'

import { SHOP_STOREFRONT, STRONGHOLD as STRONGHOLD_URL } from 'urls'
import { MAX_SET_TIMEOUT_VALUE } from 'constants'

import IndustrialResourceCoefTooltip from 'components/IndustrialResourceCoefTooltip/IndustrialResourceCoefTooltip'
import IndustrialResource from 'components/IndustrialResource/IndustrialResource'
import Timer from 'components/Timer/Timer'
import StorageTooltip from 'containers/ClanPanel/components/Storage/StorageTooltip'
import { TutorialArrow } from 'components/TutorialArrow/TutorialArrow'
import { ArrowPosition } from 'components/TutorialArrow/constants'


import { CHALLENGES, CHALLENGES_OFF, CHALLENGES_TIMER, STRONGHOLD_UPDATED } from '../translations'

import styles from './cards.scss'
import IndustrialBuyinTooltip from '../../ClanPanel/components/IndustrialBuyinTooltip/IndustrialBuyinTooltip'
import { GA_ACTIONS, GA_LABEL, trackEvent } from 'googleAnalytic'

export class StrongholdCard extends React.Component {
    static propTypes = {
        requestNextChallengeInfo: PropTypes.func.isRequired,
        openStronghold: PropTypes.func.isRequired,
        openShop: PropTypes.func.isRequired,

        challenge: PropTypes.object.isRequired,
        strongholdLevel: PropTypes.string,
        industrialResource: PropTypes.number,
        industrialResourceMultiplier: PropTypes.number.isRequired,
        industrialResourceBuyinAvailable: PropTypes.bool.isRequired,

        withNews: PropTypes.bool,
        withQuests: PropTypes.bool,
        withTutorialArrow: PropTypes.bool,

        sendEnterStrongholdSoundState: PropTypes.func.isRequired,
        playEnterStrongholdSound: PropTypes.func.isRequired,
    }

    componentDidUpdate(oldProps) {
        if (this.props.challenge.start_datetime !== oldProps.challenge.start_datetime) {
            const timeToStart = this.props.challenge.start_datetime - Date.now()
            if (timeToStart > 0) {
                clearTimeout(this.timeToChallengeTimeoutId)
                this.timeToChallengeTimeoutId = setTimeout(
                    this.props.requestNextChallengeInfo,
                    Math.min(timeToStart + 1000, MAX_SET_TIMEOUT_VALUE),
                )
            }
        }
    }

    componentWillUnmount() {
        clearTimeout(this.timeToChallengeTimeoutId)
    }

    handleOpenShop = (event) => {
        event.preventDefault()
        event.stopPropagation()

        trackEvent(GA_ACTIONS.TO_STOREFRONT, GA_LABEL.FROM_CLAN_LANDING_SCREEN)
        this.props.openShop()
    }

    handleIndustrialResourceBuyin = (event) => {
        event.preventDefault()
        event.stopPropagation()

        sendButtonClickSoundCommand()
    }

    handleOpenStronghold = () => {
        this.props.openStronghold()
        this.props.sendEnterStrongholdSoundState()
        this.props.playEnterStrongholdSound()
    }

    renderChallengesStatus() {
        const now = Date.now()
        const { start_datetime, end_datetime } = this.props.challenge

        if (!start_datetime && !end_datetime) {
            return CHALLENGES_OFF
        }
        if (now > start_datetime && now < end_datetime) {
            const timer = (
                <Timer
                    endTime={moment(end_datetime)}
                    onExpire={this.props.requestNextChallengeInfo}
                    onExpireDelay={1000}
                />
            )
            return (
                <span>
                    {CHALLENGES}
                    &nbsp;
                    {CHALLENGES_TIMER(
                        <span className={styles.timer}>{timer}</span>,
                    )}
                </span>
            )
        }

        const momentDate = moment(start_datetime)
        return (
            <span>
                {CHALLENGES}
                &nbsp;
                {getEndDateMessage(momentDate)}
            </span>
        )
    }

    render() {
        const {
            industrialResourceMultiplier,
            strongholdLevel,
            industrialResourceBuyinAvailable,
            industrialResource,
            withQuests,
            withNews,
            withTutorialArrow,
        } = this.props

        const className = classNames(styles.card, styles.stronghold, {
            [styles.stronghold__with_news]: withNews && !withQuests,
            [styles.stronghold__with_quests]: withQuests && !withNews,
            [styles.stronghold__with_news_with_quests]: withNews && withQuests,
        })
        const balanceClassName = classNames(
            styles.balance,
            industrialResource < 0 && styles.balance_notEnough,
        )

        const arrowHintClassName = (
            !withNews && !withQuests && styles.tutorialArrow
        )

        return (
            <div
                className={className}
                onClick={this.handleOpenStronghold}
                onMouseDown={sendClickSoundCommand}
                onMouseEnter={sendHoverSoundCommand}
            >
                {withTutorialArrow && (
                    <TutorialArrow
                        classNames={{
                            hint: arrowHintClassName,
                        }}
                        showBorder={false}
                        arrowPosition={ArrowPosition.Left}
                        text={STRONGHOLD_UPDATED}
                    />
                )}
                {industrialResourceMultiplier > 1 && (
                    <span
                        className={styles.multiplier}
                        data-tip data-for={IndustrialResourceCoefTooltip.id}
                    >
                        {INDUSTRIAL_RESOURCE_MULTIPLIER(industrialResourceMultiplier)}
                    </span>
                )}
                {industrialResourceMultiplier > 1 && (
                    <IndustrialResourceCoefTooltip multiplier={industrialResourceMultiplier} />
                )}
                <h2>
                    {STRONGHOLD}
                    <span className={styles.stronghold_level}>
                        {strongholdLevel}
                    </span>
                </h2>
                <h3>
                    {this.renderChallengesStatus()}
                </h3>
                <div className={balanceClassName}>
                    <IndustrialResource
                        value={industrialResource}
                        custom
                        iconAtLeft
                        notEnough={industrialResource < 0}
                        popoverId={StorageTooltip.id}
                    />
                    {industrialResourceBuyinAvailable && (
                        <div
                            className={styles.industrialResourceBuyin}
                            onMouseDown={this.handleIndustrialResourceBuyin}
                            onMouseEnter={sendHoverSoundCommand}
                            onClick={this.handleOpenShop}
                            data-tip
                            data-for={IndustrialBuyinTooltip.id}
                        />
                    )}
                    {industrialResourceBuyinAvailable && (
                        <IndustrialBuyinTooltip/>
                    )}
                    <StorageTooltip
                        industrialResource={industrialResource}
                        showFooter={false}
                    />
                </div>
            </div>
        )
    }
}

const stateToProps = state => {
    return {
        industrialResourceMultiplier: state.settings.industrialResourceMultiplier,
        industrialResource: state.clan.industrial_resource,
        industrialResourceBuyinAvailable: !!state.shop.status && state.shop.status.enabled,
        challenge: state.challenges.nextChallenge,
        strongholdLevel: toRoman(state.stronghold.level),
    }
}

const dispatchToProps = {
    openStronghold: () => push(STRONGHOLD_URL),
    openShop: () => push(SHOP_STOREFRONT),
    requestNextChallengeInfo,
    sendEnterStrongholdSoundState,
    playEnterStrongholdSound,
}

export default connect(stateToProps, dispatchToProps)(StrongholdCard)



// WEBPACK FOOTER //
// ./containers/ClanLanding/components/StrongholdCard.jsx